// Boston University: public college page (lane 2, #2745), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - BU's Common Data Set 2025-2026 (Analytical Services and Institutional
//    Research): C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table, C8 test policy for Fall 2027, C21/C22 plans with the
//    Fall 2025 early decision counts (Early Decision and Early Decision 2
//    together). The residency table also has an "unknown" column (2,159
//    applied, 349 admitted) that is not shown as its own row.
//  - BU Admissions: Important Deadlines (application and notification dates)
//    and First-Year Applicants (test policy, College of Fine Arts
//    requirements). The CDS lists November 1 as the first early decision
//    closing date; the admissions page, which says November 2, is used.
//  - BU Financial Assistance, Cost of Attendance for Undergraduates: the
//    2026/2027 resident budget. The CDS G1 figures are lower and look like
//    the 2025-2026 charges, so the financial aid office's budget is used.
//  - College Scorecard API, school id 164988 (net price by family income).
// Re-read 2026-10-30 (#3213): the CDS 2025-2026 C1 counts, residency table
// and C21 early decision counts are unchanged, and it is still the newest
// edition on BU's Common Data Set page.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Boston University Common Data Set 2025-2026',
        url: 'https://www.bu.edu/asir/files/2026/07/CDS-2025-2026-updated.pdf',
        read: '2026-10-30',
    },
    deadlines: {
        label: 'BU Admissions, Important Deadlines',
        url: 'https://www.bu.edu/admissions/apply/deadlines/',
        read: '2026-10-01',
    },
    ed: {
        label: 'BU Admissions, Early Decision Applicants',
        url: 'https://www.bu.edu/admissions/apply/early-decision/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'BU Admissions, First-Year Applicants',
        url: 'https://www.bu.edu/admissions/apply/first-year/',
        read: '2026-10-01',
    },
    costs: {
        label: 'BU Financial Assistance, Cost of Attendance for Undergraduates',
        url: 'https://www.bu.edu/finaid/undergraduate-students/undergraduate/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?164988-Boston-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'boston-university',
    kbId: 'boston_university',
    name: 'Boston University',
    shortName: 'BU',
    location: 'Boston, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (BU brand color guidelines); decoration only.
    branding: {
        primary: '#CC0000',
        secondary: '#2D2926',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/boston_university_logo.png',
    },

    // The direct answer and short FAQ (#4229, founder 2026-10-14: content
    // only, no title change). Every figure restates one the page already
    // shows from the source named beside it; CollegePages.test.jsx fails a
    // number the rest of the page does not hold.
    // The search it answers: boston university acceptance rate, and (#3213,
    // the page's next most searched) boston university acceptance rate out
    // of state.
    faq: {
        answer: { text: 'Boston University admitted 12.8% of first-year applicants for fall 2025, 9,853 of the 76,776 who applied, and 11.6% of out-of-state applicants, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1 and its residency table' },
        questions: [
            { q: 'What is the BU acceptance rate?', a: 'For fall 2025 entry, BU admitted 9,853 of 76,776 first-year applicants, an acceptance rate of 12.8%, and 3,449 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'What is the BU acceptance rate for out-of-state students?', a: 'For fall 2025, BU admitted 5,908 of 50,764 out-of-state applicants, 11.6%. For Massachusetts residents it was 1,075 of 8,059, 13.3%.', source: 'cds', where: 'section C1, residency table' },
            { q: 'What is the BU early decision acceptance rate?', a: 'For fall 2025, BU admitted 2,165 of 6,907 Early Decision and Early Decision 2 applicants, 31.3%. The Common Data Set reports the two rounds together. BU has no early action plan.', source: 'cds', where: 'sections C21 and C22' },
            { q: 'What are BU\'s application deadlines for fall 2027?', a: 'Early Decision is due November 2, with notification on December 15. Early Decision 2 and Regular Decision are both due January 5, with notification on February 9 and March 28. Both Early Decision plans are binding.', source: 'deadlines' },
            { q: 'Does BU require the SAT or ACT?', a: 'No. BU is test optional for first-year applicants through fall 2028 and spring 2029. You choose whether your scores are considered, and if you send them, BU superscores both the SAT and the ACT.', source: 'firstYear' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 76776,
        admitted: 9853,
        enrolled: 3449,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state (Massachusetts)', applied: 8059, admitted: 1075 },
            { group: 'Out-of-state', applied: 50764, admitted: 5908 },
            { group: 'International', applied: 15794, admitted: 2521 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (2,159 applicants of unknown residency not shown)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'BU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or college.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set reports Early Decision and Early Decision 2 together. BU has no early action plan.',
        rows: [
            { plan: 'Early Decision and Early Decision 2', applied: 6907, admitted: 2165 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of BU\'s schools and colleges. One asks for more:',
        rows: [
            { school: 'College of Fine Arts (art, music, theatre)', requirement: 'Program requirements such as a portfolio, an audition or an interview, set by each program.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 2', decision: 'December 15', binding: true },
            { plan: 'Early Decision 2', deadline: 'January 5', decision: 'February 9', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'March 28', binding: false },
        ],
        note: 'The CSS Profile and FAFSA are due with each plan\'s application. Some merit scholarships, music prescreening recordings and theatre prescreens are due December 1. Regular Decision enrollment deposits are due May 1.',
        source: 'deadlines',
        binding: 'Early Decision and Early Decision 2 are binding and open only to high school seniors for September admission: if admitted, you pay a nonrefundable enrollment deposit and withdraw your applications to all other colleges.',
        bindingSource: 'ed',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'BU is test optional for first-year applicants through fall 2028 and spring 2029, for every school and college and for scholarships. You choose whether your SAT or ACT scores are considered; if you send them, BU superscores both tests, and scores you enter yourself must be confirmed by an official report before you enroll.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted, for fall 2027 applicants, with scores due by January 5.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'BU stays test optional for students applying for fall 2028 entry.', source: 'firstYear' },
        ],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 73024 },
            { item: 'Fees', amount: 1570 },
            { item: 'Housing and food (most plans)', amount: 20740 },
            { item: 'Books and supplies (estimated)', amount: 1000 },
            { item: 'Incidentals (estimated)', amount: 2085 },
        ],
        total: 98419,
        note: 'BU\'s budget for a student living on campus. First-year College of General Studies students have a separate cost of attendance.',
        source: 'costs',
        netPrice: {
            average: 36503,
            byIncome: [
                { income: '$0 to $30,000', amount: 16336 },
                { income: '$30,001 to $48,000', amount: 39763 },
                { income: '$48,001 to $75,000', amount: 52880 },
                { income: '$75,001 to $110,000', amount: 70352 },
                { income: 'Over $110,000', amount: 83101 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from BU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
